import { useFormContext } from 'react-hook-form';
import { useQueryHelper } from '@anytag/shared-hooks';
import { getValuesArray } from '@anytag/shared-atoms';
import { useHubspotCompanyListLazyQuery, useHubspotCompanyNameLazyQuery } from '@anytag/graphql';
import { SelectValueType } from '../selector-field';
import { AsyncSelectorSearchField, AsyncSelectorSearchFieldProps } from '../async-selector-search-field';
import {
  HubspotCompanySearchOption,
  AsyncSearchHubspotCompanyFieldProps,
  SingleAsyncSearchHubspotCompanyFieldProps,
  MultipleAsyncSearchHubspotCompanyFieldProps
} from './types';

// Function overloads for proper type inference based on `multiple` prop
export function AsyncSearchHubspotCompanyField<T extends string>(
  props: MultipleAsyncSearchHubspotCompanyFieldProps<T>
): React.ReactElement;
export function AsyncSearchHubspotCompanyField<T extends string>(
  props: SingleAsyncSearchHubspotCompanyFieldProps<T>
): React.ReactElement;
export function AsyncSearchHubspotCompanyField<T extends string>({
  name,
  title,
  multiple,
  searchProps,
  ...restProps
}: AsyncSearchHubspotCompanyFieldProps<T>): React.ReactElement {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getHubspotCompanyList, loading: fetchingList } = useHubspotCompanyListLazyQuery();
  const { getHubspotCompanyName, loading: fetchingName } = useHubspotCompanyNameLazyQuery();
  const { watch } = useFormContext<{ [name: string]: SelectValueType<HubspotCompanySearchOption> }>();

  const value = watch(name);
  const selectedCompanies = getValuesArray({ value, multiple }) as string[];

  const fetchHubspotCompanies: AsyncSelectorSearchFieldProps<T, HubspotCompanySearchOption>['onSearch'] = async (
    keyword,
    initialLoad
  ) => {
    let companies: HubspotCompanySearchOption[] = [];

    try {
      if (initialLoad) {
        // TODO: ask BE - should implement like other async search fields
        const results = await Promise.all(
          selectedCompanies.map((id) => getHubspotCompanyName({ variables: { hubspotId: id } }))
        );
        companies = results
          .flatMap((result) => (result.data?.hubspotCompanyName ? [result.data.hubspotCompanyName] : []))
          .map((company) => ({ value: company.id, label: company.name, ...company }));
      } else {
        const { data } = await getHubspotCompanyList({ variables: { keyword: keyword ?? '' } });
        companies =
          data?.hubspotCompanyList?.map((company) => ({ value: company.id, label: company.name, ...company })) || [];
      }
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }

    return companies;
  };

  const commonProps: Pick<
    AsyncSelectorSearchFieldProps<T, HubspotCompanySearchOption>,
    'name' | 'loading' | 'onSearch' | 'title' | 'searchProps'
  > = {
    name,
    onSearch: fetchHubspotCompanies,
    loading: fetchingList || fetchingName,
    title: title ?? t<string>('Selector.Hubspot Customer'),
    searchProps: { placeholder: t<string>('Search by Customer name or Company ID'), ...searchProps }
  };

  return multiple ? (
    <AsyncSelectorSearchField<T, HubspotCompanySearchOption>
      {...(restProps as Partial<MultipleAsyncSearchHubspotCompanyFieldProps<T>>)}
      {...commonProps}
      multiple={true}
    />
  ) : (
    <AsyncSelectorSearchField<T, HubspotCompanySearchOption>
      {...(restProps as Partial<SingleAsyncSearchHubspotCompanyFieldProps<T>>)}
      {...commonProps}
      multiple={false}
    />
  );
}
